import { ChangeEvent, JSX, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import EditableText from "utils/edit/EditableText";
import { ChangeEvent as UpdateEvent } from "utils/history/Event";

import { EvaluationScoresWidget, Widget, WidgetKind } from "./DashboardTypes";
import ScoresWidgetExtension from "./ScoresWidgetExtension";

export type WidgetRowProps = {
	widget: Widget;
	editable: boolean;
	selected: boolean;

	onSelect: (uuid: string, checked: boolean) => void;
	onUpdate: (uuid: string, name: string) => void;
	onChange: (uuid: string, event: UpdateEvent) => void;
};

export default function WidgetRow(
	props: Readonly<WidgetRowProps>
): JSX.Element {
	const { t } = useTranslation();

	const [expanded, setExpanded] = useState(false);

	const extension = useMemo(() => {
		if (props.widget.type === WidgetKind.EVALUATION_SCORES) {
			return (
				<ScoresWidgetExtension
					widget={props.widget as EvaluationScoresWidget}
					editable={props.editable}
					onChange={props.onChange}
				/>
			);
		}

		return null;
	}, [props.widget]);

	const handleSelect = useCallback(
		(_event: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
			props.onSelect(props.widget.uuid, checked);
		},
		[props.widget.uuid, props.onSelect]
	);

	const handleChangeName = useCallback(
		(value: string): void => {
			props.onUpdate(props.widget.uuid, value);
		},
		[props.widget.uuid, props.onUpdate]
	);

	const toggleExpansion = useCallback(() => {
		setExpanded(prev => !prev);
	}, []);

	return (
		<>
			<TableRow sx={{ "& > td": { borderBottom: "unset" } }}>
				<TableCell align="center" padding="checkbox">
					<Checkbox
						disabled={!props.editable}
						checked={props.selected}
						onChange={handleSelect}
					/>
				</TableCell>
				<TableCell padding="checkbox">
					{extension && (
						<IconButton onClick={toggleExpansion}>
							{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						</IconButton>
					)}
				</TableCell>
				<TableCell>{t(props.widget.type)}</TableCell>
				<TableCell
					sx={{
						paddingTop: 0,
						paddingBottom: 0,
						alignContent: "center"
					}}>
					<EditableText
						value={props.widget.name}
						editable={props.editable}
						onChange={handleChangeName}
					/>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell
					style={{ paddingBottom: 0, paddingTop: 0 }}
					colSpan={4}>
					<Collapse in={expanded} timeout="auto" unmountOnExit>
						<Box sx={{ margin: 1 }}>{extension}</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
}
